import { useState, useEffect, useCallback, Fragment } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import '../styles/proposalPrint.css';

import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';

import {
  ProposalViewAwards,
  ProposalViewContacts,
  ProposalViewInstallerNotes,
  ProposalViewSystemEquipment,
  ProposalViewSystemWorks,
  ProposalViewSystemWarranties,
  ProposalViewSystemRebatesAndInstants,
  ProposalViewJobOverview,
  ProposalViewWorkDescription,
} from '../components';

import { calculateTotalPay } from '../utils/proposalCalculations';

const fontFamily = '"Inter", sans-serif';

const ProposalEmbed = () => {
  const { publicId } = useParams();
  const [searchParams] = useSearchParams();
  const apiKey = searchParams.get('key');

  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProposal = useCallback(async () => {
    if (!publicId || !apiKey) {
      setError(!apiKey ? 'API key is required (?key=...)' : 'Proposal ID is required');
      setLoading(false);
      return;
    }

    try {
      const baseUrl = window.location.origin;

      const response = await axios.get(
        `${baseUrl}/api/v1/public/proposal/embed/${publicId}?key=${encodeURIComponent(apiKey)}`
      );
      setProposal(response.data);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load proposal';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [publicId, apiKey]);

  useEffect(() => {
    loadProposal();
  }, [loadProposal]);

  // When the client accepted one of the proposal options, only that option's
  // systems are part of the contract (same filter as PrintBatch/calculateTotalPay)
  const getActiveSystems = (prop) => {
    const systems = prop.proposalSystems || [];
    return prop.optionAccepted && prop.selectedOptionId
      ? systems.filter(s => s.optionId === prop.selectedOptionId)
      : systems;
  };

  const getJobOverview = (prop) => {
    if (prop.jobOverview) return prop.jobOverview;
    const parts = getActiveSystems(prop).filter(s => s.notes).map(s => s.notes);
    if (parts.length) return parts.join('\n\n');
    return null;
  };

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2, p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>{error}</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">Loading {publicId}...</Typography>
      </Box>
    );
  }

  if (!proposal) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" color="text.secondary">Proposal not found</Typography>
      </Box>
    );
  }

  const totalPay = calculateTotalPay(proposal);

  return (
    <Fragment>
      <Box sx={{ background: '#f5f5f5', minHeight: '100vh', py: 2 }}>
        <Paper
          className="proposal-print-paper"
          elevation={1}
          sx={{
            width: 718,
            maxWidth: '100%',
            margin: '0 auto',
            padding: '10mm 10mm 10mm 10mm',
            fontFamily,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <ProposalViewAwards
            techName={proposal.createdBy?.fullName}
            techPhone={proposal.createdBy?.phoneNumber}
            proposalId={proposal.publicId}
            issueDate={proposal.createdAt}
            installationSchedule={proposal.installationSchedule}
            smallLogo={true}
          />

          <Typography
            sx={{
              fontFamily,
              fontSize: 10,
              fontWeight: 700,
              textAlign: 'center',
              color: '#666',
              mt: -1,
              mb: 1,
            }}
          >
            Document for internal use only
          </Typography>

          <ProposalViewContacts property={proposal.property} billingAddress={proposal.billingAddress} />

          {proposal.notesForInstallers?.length > 0 && (
            <ProposalViewInstallerNotes notes={proposal.notesForInstallers} />
          )}

          <ProposalViewJobOverview description={getJobOverview(proposal)} />

          {/* Work Description Title */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
            <Typography
              sx={{
                fontFamily,
                fontWeight: 600,
                fontSize: 16,
                color: '#df1f26',
                pb: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                borderBottom: '2px solid #df1f26',
              }}
            >
              WORK DESCRIPTION
            </Typography>
          </Box>

          {/* SERVICE: Work Descriptions */}
          {proposal.proposalType === 'Service' && proposal.workDescriptions?.length > 0 && (
            <ProposalViewWorkDescription items={proposal.workDescriptions} />
          )}

          {/* Old structure (legacy data) */}
          {proposal.data?.systems?.map((system, si) => (
            <Box key={si} sx={{ mb: 1.5 }}>
              {system.equipments?.length > 0 && !system.usePromptGeneration && (
                <ProposalViewSystemEquipment
                  arr={system.equipments}
                  handleChange={() => {}}
                  system_index={si}
                  title={system.name}
                  equipmentTypes={{}}
                />
              )}
              {system.scopeOfWorks?.length > 0 && (
                <ProposalViewSystemWorks arr={system.scopeOfWorks} handleChange={() => {}} system_index={si} />
              )}
              {system.warranties?.length > 0 && (
                <ProposalViewSystemWarranties arr={system.warranties} handleChange={() => {}} system_index={si} />
              )}
              {(system.instants?.length > 0 || system.rebates?.length > 0) && (
                <ProposalViewSystemRebatesAndInstants
                  rebates={system.rebates}
                  instants={system.instants}
                  handleChange={() => {}}
                  system_index={si}
                />
              )}
            </Box>
          ))}

          {/* ProposalSystems equipment (only the accepted option's systems) */}
          {getActiveSystems(proposal).length > 0 && (() => {
            const activeSystems = getActiveSystems(proposal);
            const systemsWithEquip = activeSystems
              .filter(s => s.equipments?.length > 0)
              .sort((a, b) => a.orderIndex - b.orderIndex);
            if (!systemsWithEquip.length) return null;
            const showNames = activeSystems.length > 1;

            return (
              <Box className="print-section" sx={{ mb: 1.5, px: 2 }}>
                <Box sx={{ display: 'inline-block', mb: 1 }}>
                  <Box sx={{ backgroundColor: '#0d3c61', color: '#fff', px: 2, py: 0.5 }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 13, textTransform: 'uppercase' }}>
                      Equipment
                    </Typography>
                  </Box>
                </Box>
                {systemsWithEquip.map((system, si) => (
                  <Box key={si} sx={{ mb: si < systemsWithEquip.length - 1 ? 2 : 0 }}>
                    {showNames && (
                      <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#0d3c61', borderBottom: '2px solid #e0e0e0', pb: 0.5, mb: 1, display: 'inline-block' }}>
                        {system.name}
                      </Typography>
                    )}
                    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, overflow: 'hidden' }}>
                      {system.equipments.map((eq, ei) => (
                        <Box
                          key={ei}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            borderBottom: ei < system.equipments.length - 1 ? '1px solid #e0e0e0' : 'none',
                          }}
                        >
                          <Typography sx={{ fontFamily, fontSize: 13, color: '#666', minWidth: 100 }}>
                            {eq.equipmentType || 'Equipment'}
                          </Typography>
                          <Typography sx={{ fontFamily, fontSize: 13, color: '#333', minWidth: 100 }}>
                            {eq.manufacturerName || '—'}
                          </Typography>
                          <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#0d3c61' }}>
                            {eq.modelNumber}
                          </Typography>
                          <Typography sx={{ fontFamily, fontSize: 13, color: '#999', ml: 'auto' }}>
                            (no S/N)
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            );
          })()}

          {/* Airflow */}
          {proposal.proposalType !== 'Service' && proposal.proposalAirflows?.length > 0 && (
            <Box className="print-section" sx={{ mb: 1.5, px: 2 }}>
              <Box sx={{ display: 'inline-block', mb: 0 }}>
                <Box sx={{ backgroundColor: '#0d3c61', color: '#fff', px: 2, py: 0.5 }}>
                  <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Airflow
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, borderTopLeftRadius: 0, overflow: 'hidden' }}>
                {[...proposal.proposalAirflows].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)).map((item, ai) => (
                  <Box
                    key={item.id || ai}
                    sx={{
                      p: 1.5,
                      borderBottom: ai < proposal.proposalAirflows.length - 1 ? '1px solid #e0e0e0' : 'none',
                    }}
                  >
                    <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#0d3c61', mb: item.description ? 0.5 : 0 }}>
                      {item.name}
                    </Typography>
                    {item.description && (
                      <Typography sx={{ fontFamily, fontSize: 13, color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Scope of Work */}
          {(() => {
            const allWorks = [];
            proposal.scopeOfWorks?.forEach(w => allWorks.push(w));
            getActiveSystems(proposal).forEach(s => {
              s.scopeOfWorks?.forEach(w => allWorks.push(w));
            });
            if (!allWorks.length) return null;

            return (
              <Box className="print-section" sx={{ mb: 1.5, px: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 16, color: '#0d3c61', textAlign: 'center', mb: 1 }}>
                  Scope of Work
                </Typography>
                <Box sx={{ display: 'inline-block', mb: 0 }}>
                  <Box sx={{ backgroundColor: '#0d3c61', color: '#fff', px: 2, py: 0.5 }}>
                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 13, textTransform: 'uppercase' }}>
                      Description
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1, p: 1 }}>
                  {allWorks.map((w, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.25 }}>
                      <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                        <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>✓</Typography>
                      </Box>
                      <Typography sx={{ fontFamily, fontSize: 13, color: '#333', lineHeight: 1.6 }}>
                        {w.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })()}

          {/* Total to Pay */}
          <Box className="print-section" sx={{ px: 2, mt: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#c62828',
                color: '#fff',
                px: 2,
                py: 1.5,
                borderRadius: proposal.paymentNotes ? '4px 4px 0 0' : 1,
              }}
            >
              <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 14 }}>
                Total to Pay:
              </Typography>
              <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 18 }}>
                ${totalPay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
            {proposal.paymentNotes && (
              <Box sx={{ px: 2, py: 1.5, backgroundColor: '#e8f4f8', borderRadius: '0 0 4px 4px' }}>
                <Typography sx={{ fontFamily, fontSize: 12, color: '#0d3c61', whiteSpace: 'pre-wrap' }}>
                  {proposal.paymentNotes}
                </Typography>
              </Box>
            )}
          </Box>

          {/* No foreman signature form — this is a read-only embed */}

        </Paper>
      </Box>

      <style>{`
        body { margin: 0; background: #f5f5f5; }
        .proposal-print-paper p,
        .proposal-print-paper span,
        .proposal-print-paper [class*="MuiTypography-root"] {
          font-size: 10pt !important;
          line-height: 1.4 !important;
        }
        .proposal-print-paper img:not(.proposal-logo-small):not(.proposal-logo-full) {
          max-height: 80px !important;
          max-width: 100px !important;
          object-fit: contain !important;
        }
      `}</style>
    </Fragment>
  );
};

export default ProposalEmbed;
